import CategorySelect, { CategoryModule } from '@/components/CategorySelect';
import Fallback from '@/components/Fallback';
import {
  createSubmitModal,
  createSubmitTrigger,
} from '@/components/ResourceManage';
import useAsyncFn from '@/hooks/useAsyncFn';
import { ContactPaymentResource } from '@/services/admin';
import { Interface, PickMuteable } from '@/services/api';
import { ContactPayment } from '@/services/entity';
import { Button, Form, Input, InputNumber, Table } from 'antd';
import { FormProps } from 'antd/lib/form';
import { TableProps } from 'antd/lib/table';
import format from 'date-fns/format';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';

type Entity = ContactPayment;
const resource = ContactPaymentResource;

const RecordForm: React.FC<FormProps> = (props) => {
  return (
    <Form labelCol={{ xs: 4 }} wrapperCol={{ xs: 20 }} {...props}>
      <Form.Item label="类型" name="categoryID" rules={[{ required: true }]}>
        <CategorySelect module={CategoryModule.ContactPayment} />
      </Form.Item>
      <Form.Item label="实付金额" name="amount" rules={[{ required: true }]}>
        <InputNumber />
      </Form.Item>
      <Form.Item label="付款方式" name="methodID">
        <CategorySelect module={CategoryModule.ContactPaymentMethod} />
      </Form.Item>
      <Form.Item label="分期备注" name="remark">
        <Input />
      </Form.Item>
    </Form>
  );
};

type EntityMutationFn = Interface<PickMuteable<Entity>, Entity>;

const SubmitModal = createSubmitModal<Entity>(RecordForm);
const SubmitTrigger = createSubmitTrigger(SubmitModal);

interface PaymentTableProps extends TableProps<Entity> {}
const PaymentTable: React.FC<PaymentTableProps> = () => {
  const match = useRouteMatch<{ id: string }>();

  const [state, query] = useAsyncFn(resource.query);
  React.useEffect(() => {
    query({});
  }, [query]);

  const assocProfileID: (fn: EntityMutationFn) => EntityMutationFn = (fn) => {
    return (params, config) => {
      return fn({ ...params, profileID: parseInt(match.params.id) }, config);
    };
  };

  const props: TableProps<Entity> = {
    rowKey: 'id',
    style: {
      marginBottom: 8,
    },
    title: () => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>付款</span>
        <SubmitTrigger
          ModalProps={{
            title: '添加付款',
            centered: true,
            onSubmitted: state.retry,
            submit: assocProfileID(resource.create),
          }}
          render={({ open }) => (
            <Button type="link" onClick={open}>
              添加付款
            </Button>
          )}
        />
      </div>
    ),
    bordered: true,
    size: 'small',
    columns: [
      {
        dataIndex: 'id',
        title: '#',
      },
      {
        dataIndex: 'category',
        title: '类型',
        render: (value) => value.name,
      },
      { dataIndex: 'amount', title: '金额', render: (value) => `${value}元` },
      {
        dataIndex: 'method',
        title: '付款方式',
        render: (value) => value.name,
      },
      {
        dataIndex: 'createdAt',
        title: '时间',
        render: (createdAt) => format(new Date(createdAt), 'yyyy-MM-dd'),
      },
      {
        title: '操作',
        render: (_, record) => {
          return (
            <>
              <SubmitTrigger
                ModalProps={{
                  title: '编辑付款',
                  centered: true,
                  submit: assocProfileID(resource.update(record.id)),
                  onSubmitted: state.retry,
                  FormProps: { initialValues: record },
                }}
                render={({ open }) => (
                  <Button type="link" onClick={open}>
                    编辑
                  </Button>
                )}
              />
            </>
          );
        },
      },
    ],
  };

  return (
    <Fallback
      state={state}
      initial={<Table {...props} />}
      render={(data, loading) => (
        <Table {...props} dataSource={data.data} loading={loading} />
      )}
    />
  );
};

export default PaymentTable;
